import type { PublicProductListing, PublicWidget } from "./checkout-schemas.ts";
import { deepFreeze } from "./deep-freeze.ts";
import type {
  LockerverseCatalog,
  LockerverseCommunityBranding,
  LockerversePaymentResult,
  LockerversePreparedCheckout,
  LockerverseProduct,
} from "./types.ts";

function mapProduct(listing: PublicProductListing): LockerverseProduct {
  const { product } = listing;

  return {
    acceptsDiscounts: listing.acceptsDiscounts ?? false,
    amountCents: product.amountCents,
    description: product.description ?? null,
    fulfillmentType: product.fulfillmentType,
    id: product.id,
    inventoryQuantity: product.inventoryQuantity,
    listingId: listing.id,
    maximumQuantity: listing.maxQuantityPerOrder,
    minimumAmountCents: product.minimumAmountCents,
    minimumQuantity: product.minQuantity,
    name: product.name,
    paymentOption: product.paymentOption,
    pricingMode: product.pricingMode,
    slug: listing.slug,
  };
}

function mapCommunity(
  community: PublicWidget["community"]
): LockerverseCommunityBranding | null {
  if (!community) {
    return null;
  }
  const branding: LockerverseCommunityBranding = {
    accentColor: community.color,
    imageUrl: community.thumbnail || null,
    name: community.name,
  };
  if (community.widgetAchEnabled !== undefined) {
    branding.widgetAchEnabled = community.widgetAchEnabled;
  }
  if (community.memberIdLabel !== undefined) {
    branding.memberIdLabel = community.memberIdLabel;
  }
  return branding;
}

export function createCatalog(widget: PublicWidget): LockerverseCatalog {
  const fields = widget.config?.fields ?? {};

  return {
    availability: widget.availability,
    community: mapCommunity(widget.community),
    event: widget.event
      ? {
          description: widget.event.description,
          endAt: widget.event.endAt,
          id: widget.event.id,
          location: widget.event.location,
          name: widget.event.name,
          startAt: widget.event.startAt,
          status: widget.event.status,
        }
      : null,
    payment: {
      connectedAccountId: widget.payment.connectedAccountId,
      currency: widget.payment.currency,
      publishableKey: widget.payment.publishableKey,
    },
    pricingPolicy: {
      currency: "usd",
      serviceFeeRateBasisPoints:
        widget.pricingPolicy?.serviceFeeRateBasisPoints ?? 0,
    },
    products: (widget.products ?? []).map(mapProduct),
    widget: {
      customFields: widget.config?.customFields ?? [],
      description: widget.description ?? null,
      fields: {
        memberId: fields.memberId ?? false,
        merchSize: fields.merchSize ?? false,
        phone: fields.phone ?? false,
        promoCode: fields.promoCode ?? false,
        serviceFee: fields.serviceFee ?? false,
        shipping: fields.shipping ?? false,
        tipping: fields.tipping ?? false,
      },
      id: widget.id ?? "",
      name: widget.name ?? "Lockerverse widget",
      requiredShipping: widget.config?.requiredShipping,
      showCommunityBranding: widget.showCommunityBranding ?? true,
      title: widget.title ?? null,
    },
  };
}

export function cloneCatalog(catalog: LockerverseCatalog): LockerverseCatalog {
  return cloneSnapshot(catalog);
}

export function clonePreparedCheckout(
  checkout: LockerversePreparedCheckout
): LockerversePreparedCheckout {
  return cloneSnapshot(checkout);
}

export function clonePaymentResult(
  paymentResult: LockerversePaymentResult
): LockerversePaymentResult {
  return cloneSnapshot(paymentResult);
}

function cloneSnapshot<T>(value: T): T {
  return deepFreeze(structuredClone(value));
}
